import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format as formatDate } from "date-fns";
import { ar as arLocale } from "date-fns/locale";
import { Loader2, Save, Trash2, AlertTriangle, CalendarIcon } from "lucide-react";

/**
 * MilestoneEditDialog — shared editor used from both the admin
 * Company → Program panel and the founder Dashboard.
 *
 * Props:
 *   - open, onOpenChange       : controlled visibility
 *   - milestone                : the row being edited (null → create mode)
 *   - onSave(fields)           : (title, description, stage, due_at, order?) → Promise
 *   - onDelete(entry_id)?      : shown as a destructive button; omit to hide
 *   - saving                   : boolean, disables buttons while parent is mid-request
 *   - title                    : dialog title override (defaults per mode)
 */
export function MilestoneEditDialog({ open, onOpenChange, milestone, onSave, onDelete, saving = false, title, availableStages = [] }) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const creating = !milestone;
  const [form, setForm] = useState({ title: "", description: "", stage: "", due_at: "", note: "", status: "todo" });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [customStage, setCustomStage] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      title: milestone?.title || "",
      description: milestone?.description || "",
      stage: milestone?.stage || "",
      due_at: milestone?.due_at || "",
      note: milestone?.note || "",
      status: milestone?.status || "todo",
    });
    setConfirmDelete(false);
    // Start in Select mode if there's a shared vocabulary; but if the
    // current milestone's stage isn't in the vocabulary, expose the
    // free-text fallback so admins can keep their value.
    const inVocab = !milestone?.stage || availableStages.includes(milestone.stage);
    setCustomStage(!inVocab);
  }, [open, milestone, availableStages]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) return;
    await onSave({
      title: form.title.trim(),
      description: form.description,
      stage: form.stage.trim(),
      due_at: form.due_at,
      note: form.note,
      ...(creating ? {} : { status: form.status }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="milestone-edit-dialog">
        <DialogHeader>
          <DialogTitle>{title || (creating ? t("milestones.add", { defaultValue: "Add milestone" }) : t("milestones.edit", { defaultValue: "Edit milestone" }))}</DialogTitle>
          <DialogDescription className="text-xs">
            {creating
              ? t("milestones.dialog.createHint", { defaultValue: "Add a milestone attached to this company only — the shared program template is untouched." })
              : t("milestones.dialog.editHint", { defaultValue: "Edits update this company's snapshot only. Other companies enrolled in the same program are unaffected." })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">{t("milestones.dialog.title", { defaultValue: "Title" })}</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={t("milestones.dialog.titlePlaceholder", { defaultValue: "e.g. Register commercial license" })}
              className="rounded-xl mt-1"
              autoFocus
              data-testid="milestone-edit-title"
            />
          </div>
          <div>
            <Label className="text-xs">{t("milestones.dialog.description", { defaultValue: "Description" })}</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t("milestones.dialog.descriptionPlaceholder", { defaultValue: "Optional detail — what completing this milestone looks like." })}
              className="rounded-xl mt-1 min-h-[80px]"
              data-testid="milestone-edit-description"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("milestones.dialog.stage", { defaultValue: "Stage" })}</Label>
              {(availableStages.length === 0 || customStage) ? (
                <Input
                  value={form.stage}
                  onChange={(e) => setForm({ ...form, stage: e.target.value })}
                  placeholder={t("milestones.dialog.stagePlaceholder", { defaultValue: "e.g. sourced, diligence" })}
                  className="rounded-xl mt-1"
                  data-testid="milestone-edit-stage"
                />
              ) : (
                <Select
                  value={form.stage || "_none"}
                  onValueChange={(v) => {
                    if (v === "_custom") { setCustomStage(true); return; }
                    setForm({ ...form, stage: v === "_none" ? "" : v });
                  }}
                >
                  <SelectTrigger className="rounded-xl mt-1 h-10" data-testid="milestone-edit-stage">
                    <SelectValue placeholder={t("milestones.dialog.pickStage", { defaultValue: "Pick a stage…" })} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— {t("milestones.noStage", { defaultValue: "Unstaged" })} —</SelectItem>
                    {availableStages.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`milestone-edit-stage-opt-${s}`}>
                        {s.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                    <SelectItem value="_custom" data-testid="milestone-edit-stage-custom">+ {t("milestones.dialog.customStage", { defaultValue: "Custom stage…" })}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-xs">{t("milestones.dialog.dueDate", { defaultValue: "Due date" })}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="rounded-xl mt-1 h-10 w-full justify-start gap-2 font-normal" data-testid="milestone-edit-due-at">
                    <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className={form.due_at ? "font-mono text-xs" : "text-muted-foreground text-xs"}>
                      {form.due_at || t("milestones.dialog.dueDate", { defaultValue: "Due date" })}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" collisionPadding={12} className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={form.due_at ? new Date(`${form.due_at}T00:00:00`) : undefined}
                    onSelect={(d) => setForm({ ...form, due_at: d ? formatDate(d, "yyyy-MM-dd") : "" })}
                    locale={isAr ? arLocale : undefined}
                    dir={isAr ? "rtl" : "ltr"}
                    initialFocus
                  />
                  {form.due_at && (
                    <div className="border-t border-border p-2">
                      <Button type="button" variant="ghost" size="sm" className="w-full text-xs" onClick={() => setForm({ ...form, due_at: "" })} data-testid="milestone-edit-due-clear">
                        {t("memberTasks.clearDate", { defaultValue: "Clear date" })}
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          </div>
          {!creating && (
            <div>
              <Label className="text-xs">{t("milestones.dialog.status", { defaultValue: "Status" })}</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-xl mt-1 h-10" data-testid="milestone-edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todo">{t("milestones.status.todo", { defaultValue: "To do" })}</SelectItem>
                  <SelectItem value="doing">{t("milestones.status.doing", { defaultValue: "In progress" })}</SelectItem>
                  <SelectItem value="done">{t("milestones.status.done", { defaultValue: "Done" })}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">{t("milestones.dialog.note", { defaultValue: "Progress note" })}</Label>
            <Textarea
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder={t("milestones.notePlaceholder", { defaultValue: "Progress note — where does this stand?" })}
              className="rounded-xl mt-1 min-h-[60px]"
              data-testid="milestone-edit-note"
            />
          </div>
          <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
            {!creating && onDelete && (
              !confirmDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  className="rounded-xl gap-1 mr-auto"
                  onClick={() => setConfirmDelete(true)}
                  disabled={saving}
                  data-testid="milestone-edit-delete"
                >
                  <Trash2 className="h-4 w-4" /> {t("common.delete", { defaultValue: "Delete" })}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="destructive"
                  className="rounded-xl gap-1 mr-auto animate-pulse"
                  onClick={() => onDelete(milestone.entry_id)}
                  disabled={saving}
                  data-testid="milestone-edit-delete-confirm"
                >
                  <AlertTriangle className="h-4 w-4" /> {t("milestones.dialog.confirmDelete", { defaultValue: "Click again to confirm" })}
                </Button>
              )
            )}
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)} disabled={saving}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" className="btn-brand rounded-xl gap-1" disabled={saving || !form.title.trim()} data-testid="milestone-edit-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {creating ? t("milestones.add", { defaultValue: "Add milestone" }) : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default MilestoneEditDialog;
